import { Controller } from "@hotwired/stimulus";
import {
  fetchFavorites,
  addFavorite,
  removeFavorite,
  openSignIn,
  stashPendingFavorite,
  peekPendingFavorite,
  clearPendingFavorite,
} from "../favorites/api";

// Listing-favorite hearts on server-rendered Connect pages (SUR-2374).
// Mounted once on <body>: one delegated click handler + one hydration probe
// cover every [data-favorite-key] button (search cards, area grids, the
// listing-detail save button). React-island hearts (SearchMapIsland) manage
// themselves — clicks originating inside [data-connect-island] are ignored.
//
// Server-rendered hearts are always "unfavorited" (pages are cacheable);
// signed-in state + favorited keys hydrate from one GET /account/favorites,
// whose 401 doubles as the signed-out probe. Anonymous tap → stash the key in
// sessionStorage → open the header sign-in modal; the passwordless flow
// reloads this page, and hydrate() consumes the stash.
export default class extends Controller {
  connect() {
    this.signedIn = null; // unknown until the probe returns
    this.keys = new Set();
    // key -> the state the user last asked for. Merged over the probe's
    // snapshot so a hydration that lands mid-or-post-toggle can't repaint a
    // tapped heart backwards; dropped only when a toggle fails (server truth).
    this.overrides = new Map();
    this.syncing = new Set(); // keys with a mutation loop in flight
    this.onClick = (event) => this.handleClick(event);
    this.element.addEventListener("click", this.onClick);
    this.hydrate();
  }

  disconnect() {
    this.element.removeEventListener("click", this.onClick);
  }

  async hydrate() {
    const state = await fetchFavorites();
    if (!state) return; // network/server hiccup — leave hearts untouched
    this.signedIn = state.signedIn;
    this.keys = state.keys;
    // The probe's snapshot predates any toggle that raced it — re-apply those
    // flips so hydration can't repaint a tapped heart backwards.
    this.overrides.forEach((desired, key) => {
      if (desired) this.keys.add(key);
      else this.keys.delete(key);
    });
    this.paintAll();
    if (this.signedIn) await this.consumePending();
    // Signed out: leave any pending stash in place for the post-sign-in reload.
  }

  // Replay the heart tapped before sign-in (stashed by handleClick below).
  // The stash is cleared only after the POST persists — a transient failure
  // keeps it alive so the next page load retries.
  async consumePending() {
    const pending = peekPendingFavorite();
    if (!pending || this.keys.has(pending.key)) {
      clearPendingFavorite(); // done (or malformed) — drop the stash
      return;
    }
    const result = await addFavorite(pending.key, pending.listing);
    if (result !== "ok") return;
    clearPendingFavorite();
    this.keys.add(pending.key);
    this.paintAll();
    // React-island hearts hydrate from their own (possibly earlier) probe —
    // announce the replay so they don't miss the just-persisted favorite.
    window.dispatchEvent(
      new CustomEvent("connect:favorite-replayed", { detail: { key: pending.key } }),
    );
  }

  handleClick(event) {
    const btn = event.target.closest("[data-favorite-key]");
    if (!btn || !this.element.contains(btn)) return;
    if (btn.closest("[data-connect-island]")) return; // React owns those hearts
    event.preventDefault();
    event.stopPropagation(); // hearts sit inside card <a> links
    this.toggle(btn);
  }

  async toggle(btn) {
    const key = btn.dataset.favoriteKey;
    if (!key) return;

    if (this.signedIn === false) {
      stashPendingFavorite(key, this.snapshotFrom(btn));
      openSignIn();
      return;
    }

    // Optimistic flip (also covers signedIn === null, i.e. a tap that beat
    // the probe): instant feedback, revert on failure.
    const desired = !this.keys.has(key);
    this.overrides.set(key, desired);
    if (desired) this.keys.add(key);
    else this.keys.delete(key);
    this.paintAll();
    this.syncKey(key, btn);
  }

  // One mutation in flight per key, re-issued until the server matches the
  // user's latest desired state. Rapid re-taps must not fire concurrent
  // POST/DELETE for the same key — they can execute out of order server-side,
  // leaving the server favorited while the heart shows unfavorited.
  async syncKey(key, btn) {
    if (this.syncing.has(key)) return; // the active loop reconciles new taps
    this.syncing.add(key);
    try {
      let acked; // the server state this loop has confirmed
      for (;;) {
        const desired = this.overrides.get(key);
        if (desired === undefined || desired === acked) return;
        const result = desired
          ? await addFavorite(key, this.snapshotFrom(btn))
          : await removeFavorite(key);
        if (result === "ok") {
          // Keep the override — a stale probe snapshot must not undo it.
          acked = desired;
          continue;
        }

        // Failure: revert the optimistic flip, back to server truth.
        this.overrides.delete(key);
        if (desired) this.keys.delete(key);
        else this.keys.add(key);
        this.paintAll();
        if (result === "unauthorized") {
          this.signedIn = false;
          stashPendingFavorite(key, this.snapshotFrom(btn));
          openSignIn();
        }
        return;
      }
    } finally {
      this.syncing.delete(key);
    }
  }

  // Display snapshot the server stores with the favorite (favorite lists /
  // agent timeline). All optional — emitted as data attributes by the card
  // markup in the liquid templates.
  snapshotFrom(btn) {
    const d = btn.dataset;
    const listing = {
      address: d.favoriteAddress,
      price: d.favoritePrice,
      mls_number: d.favoriteMls,
      url: d.favoriteUrl,
      hero_photo: d.favoritePhoto,
    };
    Object.keys(listing).forEach((k) => { if (!listing[k]) delete listing[k]; });
    return Object.keys(listing).length ? listing : null;
  }

  paintAll() {
    this.element.querySelectorAll("[data-favorite-key]").forEach((btn) => {
      if (btn.closest("[data-connect-island]")) return;
      const active = this.keys.has(btn.dataset.favoriteKey);
      btn.classList.toggle("is-active", active);
      btn.setAttribute("aria-pressed", active ? "true" : "false");
      // Icon-only card hearts carry an aria-label; keep it in sync so AT
      // announces the available action, not the stale one.
      if (btn.hasAttribute("aria-label")) {
        btn.setAttribute("aria-label", active ? "Remove from saved homes" : "Save this home");
      }
      const label = btn.querySelector("[data-fav-label]");
      if (label) label.textContent = active ? "Saved" : "Save this home";
    });
  }
}
